<template>
  <div class="email-input" :class="{ 'is-editor': isEditor }" ref="emailWrapper">
    <div class="email-input__label" v-if="fields.outerLabel?.key" :id="labelId">
      <TipTap v-model="outerLabelValue" mode="single" />
    </div>
    <BaseInputShell v-bind="shellProps" :error="hasError">
      <BaseInputField
        inputmode="email"
        name="email"
        autocomplete="email"
        autocapitalize="none"
        :spellcheck="false"
        :strip-whitespace="true"
        :modelValue="value"
        @update:modelValue="onInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
        @submit="handleSubmit"
        :placeholder="placeholderText"
        :autofocus="fields.autoFocus"
        :aria-labelledby="fields.outerLabel?.key ? labelId : undefined"
        :aria-describedby="describedBy(showValidationMessage && errorId)"
        :aria-invalid="hasError"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="hasSuggestions"
        :aria-controls="hasSuggestions ? suggestionsId : undefined"
        :aria-activedescendant="activeSuggestionId"
        v-bind="fieldProps"
      />
    </BaseInputShell>
    <div
      class="email-input__typo"
      v-if="
        isEditor &&
        closestMatch &&
        closestMatch !== value &&
        suggestions.length === 0 &&
        fields.closestMatchMessage?.key
      "
    >
      <TipTap v-model="closestMatchMessageValue" mode="single" />
    </div>
    <div
      class="email-input__typo"
      v-else-if="
        closestMatch &&
        closestMatch !== value &&
        suggestions.length === 0 &&
        fields.closestMatchMessage?.key
      "
      role="button"
      tabindex="0"
      @click="applyClosestMatch"
      @keydown.enter.prevent="applyClosestMatch"
      @keydown.space.prevent="applyClosestMatch"
    >
      <TipTap v-model="closestMatchMessageValue" mode="single" />
    </div>
    <div class="email-input__error" v-if="showValidationMessage" :id="errorId">
      <TipTap v-model="validationMessageValue" mode="single" />
    </div>
    <div
      class="suggestions"
      :class="{ 'is-floating': floating }"
      v-if="suggestions.length > 0"
      ref="suggestionsEl"
    >
      <!-- The arrow keys already move `selectedIndex`; without the listbox /
           option roles and `aria-activedescendant` on the input above, that
           movement is visible only as a CSS class and a screen reader hears
           neither that suggestions appeared nor which one is active. -->
      <ul :id="suggestionsId" role="listbox">
        <li
          v-for="(suggestion, index) in suggestions"
          :key="index"
          :id="suggestionId(index)"
          role="option"
          :aria-selected="index === selectedIndex"
          :class="{ active: index === selectedIndex }"
          @mousedown="() => pickSuggestion(suggestion)"
          @mouseover="selectedIndex = index"
        >
          {{ suggestion }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick, useId, watch } from 'vue'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useFormInput } from '@render/composables/useFormInput'
import { useEmailCheck } from '@render/composables/useEmailCheck'
import { useTranslation } from '@render/composables/useTranslation'
import { useInputAria } from '@render/composables/useInputAria'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import { getColorStyleData, getSizeStyleData } from '@render/composables/useStyleData'
import { tiptapToText } from '@render/lib/tiptapToText'
import BaseInputShell from '../BaseInput/BaseInputShell.vue'
import BaseInputField from '../BaseInput/BaseInputField.vue'
import TipTap from '@render/lib/TipTap'
import type { EmailInputFields } from './definition'

const emit = defineEmits<{
  (e: 'onSubmit'): void
}>()

// Ordered by real-world popularity (most popular first). Suggestion ranking
// relies on this order, so keep new entries in their popularity position.
const popularDomains = ref([
  'gmail.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'wp.pl',
  'outlook.com',
  'web.de',
  'gmx.de',
  'hotmail.fr',
  'libero.it',
  'aol.com',
  'o2.pl',
  'yahoo.fr',
  'hotmail.it',
  'yahoo.it',
  'live.com',
  'ukr.net',
  'gmx.at',
  'hotmail.co.uk',
  'msn.com',
  'yahoo.es',
  'orange.fr',
  'op.pl',
  'googlemail.com',
  'outlook.fr',
  'hotmail.es',
  'live.fr',
  'yahoo.de',
  'gmx.net',
  'yahoo.co.uk',
  'virgilio.it',
  'yahoo.com.mx',
  'comcast.net',
  'hotmail.de',
  'ymail.com',
  'onet.pl',
  'live.it',
  'yahoo.com.ar',
  'tiscali.it',
  'yahoo.gr',
  'protonmail.com',
])

const suggestions = ref<string[]>([])
const selectedIndex = ref(-1)
const emailWrapper = ref<HTMLElement | null>(null)
const suggestionsEl = ref<HTMLElement | null>(null)

const ensureVisible = () => {
  setTimeout(() => {
    if (emailWrapper.value) {
      emailWrapper.value.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, 300)
}

const ensureSuggestionsVisible = () => {
  if (!floating.value) return
  nextTick(() => {
    suggestionsEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  })
}

const { closestMatch, checkTypo, getAutocompleteSuggestions, isBasicValid, cleanEmailValue } =
  useEmailCheck(popularDomains)

useComponentScopedVariables('EmailInput', () => ({
  closestMatch: closestMatch.value,
}))

const handleFocus = () => {
  ensureVisible()
  // With realtime validation off, post-submit errors are the only way the
  // user has seen the red state. Refocusing to fix should clear it; the
  // next submit re-evaluates.
  if (fields.value.disableRealtimeValidation) formInput.clearTouched()
}

const handleBlur = () => {
  checkTypo(value.value || '')
  suggestions.value = []
  selectedIndex.value = -1
  if (value.value !== '') formInput.handleBlur()
}

const handleKeydown = (e: KeyboardEvent) => {
  if (suggestions.value.length === 0) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value + 1) % suggestions.value.length
      break
    case 'ArrowUp':
      e.preventDefault()
      selectedIndex.value =
        selectedIndex.value - 1 < 0 ? suggestions.value.length - 1 : selectedIndex.value - 1
      break
    case 'Enter':
      if (selectedIndex.value >= 0) {
        e.preventDefault()
        pickSuggestion(suggestions.value[selectedIndex.value] || '')
      }
      break
    case 'Escape':
      e.preventDefault()
      suggestions.value = []
      selectedIndex.value = -1
      break
  }
}

const pickSuggestion = (suggestion: string) => {
  value.value = suggestion
  suggestions.value = []
  selectedIndex.value = -1
}

const applyClosestMatch = () => {
  if (closestMatch.value) {
    value.value = closestMatch.value
  }
}

const fields = defineModel<EmailInputFields>('fields', {
  required: true,
  default: () => ({
    placeholder: undefined,
    validations: [],
    validationMessage: undefined,
    valueVariable: undefined,
    isValidVariable: undefined,
  }),
})

const pipe = usePipe()
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

const outerLabelValue = useTranslation(() => fields.value.outerLabel?.key)
const validationMessageValue = useTranslation(() => fields.value.validationMessage?.key)
const closestMatchMessageValue = useTranslation(() => fields.value.closestMatchMessage?.key)
const placeholderText = computed(() =>
  tiptapToText(pipe.getTranslation(fields.value.placeholder?.key)),
)

const floating = computed(() => fields.value.floatingSuggestions !== false)

const validators: Record<string, (v: string) => boolean> = {
  notEmpty: (v) => !!v && v.trim().length > 0,
}

function isValid(v: string): boolean {
  if (!isBasicValid(v)) return false
  const selected = fields.value.validations ?? []
  return selected.every((id) => validators[id]?.(v) ?? true)
}

const value = computed<string>({
  get() {
    return fields.value.valueVariable
      ? ((pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`) ?? '') as string)
      : ''
  },
  set(val) {
    if (!fields.value.valueVariable) return
    // Transient: keep the reactive store live (suggestions, validity, CTA state)
    // but defer the localStorage persist until the form is submitted.
    pipe.setData(`var.${fields.value.valueVariable?.key ?? ''}`, val, { persist: false })
  },
})

const formInput = useFormInput({
  validate: () => isValid(value.value),
  disableRealtimeValidation: () => fields.value.disableRealtimeValidation === true,
  persistKeys: () => [fields.value.valueVariable?.key, fields.value.isValidVariable?.key],
})
const { showErrors, markTouched, requestSubmit } = formInput

const hasError = computed(() => showErrors.value && !isValid(value.value))

// The author's label and error copy sit OUTSIDE the <label> shell, so they are
// associated by id rather than by wrapping — see `useInputAria`.
const { labelId, errorId, describedBy } = useInputAria()
const showValidationMessage = computed(() => hasError.value && !!validationMessageValue.value)

// Autocomplete popup — see the listbox comment in the template.
const suggestionsUid = useId()
const suggestionsId = `${suggestionsUid}-suggestions`
const suggestionId = (index: number) => `${suggestionsUid}-suggestion-${index}`
const hasSuggestions = computed(() => suggestions.value.length > 0)
const activeSuggestionId = computed(() =>
  hasSuggestions.value && selectedIndex.value >= 0 ? suggestionId(selectedIndex.value) : undefined,
)

// Effective validity surfaced to the wired `isValidVariable`. When realtime
// validation is off, the input presents as valid except during the post-submit
// error window — so a CTA bound to this variable via `isEnabledVariable` stays
// enabled until a failed submit, becomes disabled while the error shows, and
// re-enables as soon as the user acknowledges by focusing or modifying the
// field (both call `formInput.clearTouched()`).
const effectiveIsValid = computed(() => {
  const actual = isValid(value.value)
  if (!fields.value.disableRealtimeValidation) return actual
  return !showErrors.value || actual
})

watch(
  () => [fields.value.isValidVariable?.key, effectiveIsValid.value] as const,
  ([key, valid]) => {
    // Validity is a derived/transient flag — keep it reactive for CTA enable
    // state but don't persist it until submit commits the value alongside it.
    if (key) pipe.setData(`var.${key}`, valid, { persist: false })
  },
  { immediate: true },
)

onMounted(async () => {
  if (!fields.value.valueVariable) return
  await pipe.whenReady()
  // Sanitize any persisted/external value so isBasicValid can rely on the invariant.
  const initial = value.value
  if (initial) {
    const { cleaned } = cleanEmailValue(initial, initial.length)
    if (cleaned !== initial) value.value = cleaned
  }
})

function onInput(val: number | string) {
  // Modify-to-recover: when realtime validation is off, typing after a
  // failed submit acknowledges the error (mirrors handleFocus). Without
  // this branch, a soft-keyboard Enter submit leaves the field focused so
  // no focus event re-fires when the user starts editing.
  if (fields.value.disableRealtimeValidation) formInput.clearTouched()

  const raw = String(val)
  const inputEl = emailWrapper.value?.querySelector('input') ?? null
  const cursorBefore = inputEl?.selectionStart ?? raw.length
  const { cleaned, cursor: newCursor } = cleanEmailValue(raw, cursorBefore)

  value.value = cleaned

  // Force-sync the DOM when it disagrees with `cleaned`. Vue's `:value` diff skips
  // the patch whenever `cleaned` equals the previous model value — e.g., typing
  // '@' on top of '@' both resolve to '@', so without this the dirty '@@' stays
  // in the input until the next keystroke. Also restores the cursor that the
  // direct write would otherwise drop to the end.
  if (inputEl && inputEl.value !== cleaned) {
    inputEl.value = cleaned
    inputEl.setSelectionRange(newCursor, newCursor)
  }

  suggestions.value = getAutocompleteSuggestions(cleaned, 3)
  selectedIndex.value = -1
  nextTick(() => {
    if (suggestions.value.length > 0) {
      ensureVisible()
      ensureSuggestionsVisible()
    }
  })
}

async function handleSubmit() {
  const { targetInvoked } = await requestSubmit()
  if (targetInvoked) return
  // Legacy fallback — no CTA in scope, preserve pre-Form behavior so
  // existing pipes wiring `onSubmit` directly on this input keep working.
  if (!isValid(value.value)) {
    markTouched()
    return
  }
  emit('onSubmit')
}

const overrideLabelColor = computed(
  () => getColorStyleData(fields.value.outerLabelColor) ?? 'var(--color-text-secondary)',
)
const overrideLabelFontSize = computed(
  () => getSizeStyleData(fields.value.outerLabelFontSize) ?? 'var(--text-body-sm-size)',
)
const overrideLabelLineHeight = computed(
  () => getSizeStyleData(fields.value.outerLabelLineHeight) ?? 'var(--text-body-sm-line-height)',
)
const overrideLabelFontWeight = computed(
  () => fields.value.outerLabelFontWeight ?? 'var(--text-body-sm-weight)',
)
const overrideLabelTextAlign = computed(() => fields.value.outerLabelTextAlign ?? 'left')
const overrideLabelMarginBottom = computed(
  () => getSizeStyleData(fields.value.outerLabelMarginBottom) ?? 'var(--space-8)',
)
const overrideBgColor = computed(() => getColorStyleData(fields.value.bgColor))
const overrideBgColorError = computed(() => getColorStyleData(fields.value.bgColorError))
const overrideTextColor = computed(() => getColorStyleData(fields.value.textColor))
const overrideTextColorDisabled = computed(() => getColorStyleData(fields.value.textColorDisabled))
const overrideTextModeFontSize = computed(() => getSizeStyleData(fields.value.textModeFontSize))
const overrideTextModeLineHeight = computed(() => getSizeStyleData(fields.value.textModeLineHeight))
const overridePlaceholderColor = computed(() => getColorStyleData(fields.value.placeholderColor))
const overrideBorderColor = computed(() => getColorStyleData(fields.value.borderColor))
const overrideBorderColorHover = computed(() => getColorStyleData(fields.value.borderColorHover))
const overrideBorderColorActive = computed(() => getColorStyleData(fields.value.borderColorActive))
const overrideBorderColorError = computed(() => getColorStyleData(fields.value.borderColorError))
const overrideBorderRadius = computed(() => getSizeStyleData(fields.value.borderRadius))
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight))
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth) ?? '100%')
const overridePadding = computed(() => getSizeStyleData(fields.value.padding))
const overrideSuggestionBgHover = computed(
  () => getColorStyleData(fields.value.suggestionBgHover) ?? 'var(--color-background-subtle)',
)

const shellProps = computed(() => ({
  bgColor: overrideBgColor.value,
  bgColorError: overrideBgColorError.value,
  borderColor: overrideBorderColor.value,
  borderColorHover: overrideBorderColorHover.value,
  focusBorderColor: overrideBorderColorActive.value,
  borderColorError: overrideBorderColorError.value,
  borderRadius: overrideBorderRadius.value,
  minHeight: overrideMinHeight.value,
  maxWidth: overrideMaxWidth.value,
  padding: overridePadding.value,
}))

const fieldProps = computed(() => ({
  textColor: overrideTextColor.value,
  textColorDisabled: overrideTextColorDisabled.value,
  textColorPlaceholder: overridePlaceholderColor.value,
  textModeFontSize: overrideTextModeFontSize.value,
  textModeLineHeight: overrideTextModeLineHeight.value,
  textModeFontWeight: fields.value.textModeFontWeight,
}))
</script>

<style scoped lang="scss">
.email-input {
  position: relative;

  &__label {
    max-width: v-bind(overrideMaxWidth);
    margin-left: auto;
    margin-right: auto;
    margin-bottom: v-bind(overrideLabelMarginBottom);
    color: v-bind(overrideLabelColor);
    font-size: v-bind(overrideLabelFontSize);
    font-weight: v-bind(overrideLabelFontWeight);
    line-height: v-bind(overrideLabelLineHeight);
    text-align: v-bind(overrideLabelTextAlign);
  }

  &__error {
    margin-top: var(--space-4);
    font-size: var(--text-body-sm-size);
    line-height: var(--text-body-sm-line-height);
    text-align: center;
    color: var(--color-feedback-error-text);
  }

  &__typo {
    margin: var(--space-8) 0;
    font-size: var(--text-body-sm-size);
    line-height: var(--text-body-sm-line-height);
    text-align: center;
    color: var(--color-text-link);
    font-weight: var(--text-button-weight);
    cursor: pointer;
    outline: none;

    &:hover,
    &:focus-visible {
      color: var(--color-text-link-hover);
      text-decoration: underline;
    }
  }

  &.is-editor &__typo {
    cursor: text;

    &:hover {
      color: var(--color-text-link);
      text-decoration: none;
    }
  }

  .suggestions {
    margin-top: var(--space-4);
    border: 1px solid v-bind(overrideBorderColor);
    border-radius: v-bind(overrideBorderRadius);
    background-color: var(--color-background-surface);
    box-shadow: var(--shadow-surface-raised);
    overflow: hidden;

    &.is-floating {
      position: absolute;
      top: calc(100% + var(--space-4));
      left: 0;
      right: 0;
      margin-top: 0;
      z-index: 10;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        padding: var(--space-12) var(--space-16);
        color: var(--color-text-secondary);
        font-family: var(--text-input-family);
        font-size: var(--text-input-size);
        line-height: var(--text-input-line-height);
        font-weight: var(--text-input-weight);
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover,
        &.active {
          background-color: v-bind(overrideSuggestionBgHover);
          color: v-bind(overrideTextColor);
        }
      }
    }
  }
}
</style>
